import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import CardImage from '../CardImage';

// Icons
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#facc15" stroke="#facc15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const MutualIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#8b5cf6" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const TradeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 16V4M7 4L3 8M7 4l4 4" />
    <path d="M17 8v12m0 0 4-4m-4 4-4-4" />
  </svg>
);

const WishListMatchModal = ({
  isOpen,
  onClose,
  theyHaveMatches = [], // Cards THEY have that YOU want
  youHaveMatches = [],  // Cards YOU have that THEY want
  ownerUsername,
  ownerId,
}) => {
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();

  // Default to first available tab
  const getDefaultTab = () => {
    if (theyHaveMatches.length > 0) return 'theyHave';
    if (youHaveMatches.length > 0) return 'youHave';
    return 'theyHave';
  };
  const [activeTab, setActiveTab] = useState(getDefaultTab());

  // Check for mutual matches - same card_id in both arrays
  const mutualMatches = theyHaveMatches.filter(theirCard =>
    youHaveMatches.some(yourCard => yourCard.card_id === theirCard.card_id)
  );
  const hasMutualMatches = mutualMatches.length > 0;

  if (!isOpen) return null;

  const handleRequestTrade = (card) => {
    // Navigate to the card detail page with trade request intent
    navigate(`/card/${card.id}?action=trade`);
    onClose();
  };

  const handleViewYourCard = (card) => {
    // Navigate to view your own card
    navigate(`/card/${card.id}`);
    onClose();
  };

  // Get current cards based on active tab
  const getCurrentCards = () => {
    switch (activeTab) {
      case 'theyHave':
        return theyHaveMatches;
      case 'youHave':
        return youHaveMatches;
      case 'mutual':
        return mutualMatches;
      default:
        return theyHaveMatches;
    }
  };

  const currentCards = getCurrentCards();

  // Dynamic styles
  const styles = getStyles(theme, isDark);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            {hasMutualMatches ? <MutualIcon /> : <StarIcon />}
            <h2 style={styles.title}>
              {hasMutualMatches ? 'Mutual Match!' : 'Wish List Matches!'}
            </h2>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Tabs - only show if we have cards in multiple directions */}
        {(theyHaveMatches.length > 0 || youHaveMatches.length > 0) && (
          <div style={styles.tabContainer}>
            {theyHaveMatches.length > 0 && (
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'theyHave' ? styles.tabActive : {}),
                }}
                onClick={() => setActiveTab('theyHave')}
              >
                You Want ({theyHaveMatches.length})
              </button>
            )}
            {youHaveMatches.length > 0 && (
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'youHave' ? styles.tabActiveGreen : {}),
                }}
                onClick={() => setActiveTab('youHave')}
              >
                They Want ({youHaveMatches.length})
              </button>
            )}
            {hasMutualMatches && (
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'mutual' ? styles.tabActivePurple : {}),
                }}
                onClick={() => setActiveTab('mutual')}
              >
                Mutual ({mutualMatches.length})
              </button>
            )}
          </div>
        )}

        {/* Subtitle based on tab */}
        <p style={styles.subtitle}>
          {activeTab === 'theyHave' && (
            <>@{ownerUsername || 'This user'} has {theyHaveMatches.length} card{theyHaveMatches.length !== 1 ? 's' : ''} you want</>
          )}
          {activeTab === 'youHave' && (
            <>You have {youHaveMatches.length} card{youHaveMatches.length !== 1 ? 's' : ''} @{ownerUsername || 'they'} want{youHaveMatches.length === 1 ? 's' : ''}</>
          )}
          {activeTab === 'mutual' && (
            <>You both want the same {mutualMatches.length} card{mutualMatches.length !== 1 ? 's' : ''}!</>
          )}
        </p>

        {/* Cards List */}
        <div style={styles.cardsList}>
          {currentCards.map((card) => (
            <div key={card.id} style={styles.cardItem}>
              <div style={styles.cardImageWrapper}>
                <CardImage
                  digitalImage={card.digital_image_url}
                  userPhoto={card.original_image_url}
                  name={card.card_name}
                  number={card.card_id?.split('-')[1]}
                  setName={card.set_name}
                  theme={theme}
                  imgStyle={styles.cardImage}
                />
                {/* Mutual badge on card */}
                {activeTab !== 'mutual' && mutualMatches.some(m => m.card_id === card.card_id) && (
                  <div style={styles.mutualBadge}>
                    <MutualIcon />
                  </div>
                )}
              </div>
              <div style={styles.cardInfo}>
                <span style={styles.cardName}>{card.card_name}</span>
                <span style={styles.cardSet}>{card.set_name}</span>
                {card.grade && (
                  <span style={styles.cardGrade}>{card.grade}</span>
                )}
                <span style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  color: card.trading_status === 'TRADE' ? theme.success : theme.textMuted,
                }}>
                  {card.trading_status === 'TRADE' ? 'For Trade' : 'PC'}
                </span>
              </div>
              {/* Different button based on whose card it is */}
              {activeTab === 'theyHave' || activeTab === 'mutual' ? (
                <button
                  style={styles.tradeButton}
                  onClick={() => handleRequestTrade(card)}
                >
                  <TradeIcon />
                  <span>Trade</span>
                </button>
              ) : (
                <button
                  style={styles.viewButton}
                  onClick={() => handleViewYourCard(card)}
                >
                  <span>View</span>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const getStyles = (theme, isDark) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    paddingTop: 'calc(20px + var(--safe-area-top, 0px))',
    paddingBottom: 'calc(20px + var(--safe-area-bottom, 0px))',
  },
  modal: {
    borderRadius: '20px',
    width: '100%',
    maxWidth: '400px',
    maxHeight: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.bgSheet,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: `1px solid ${theme.border}`,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    color: theme.text,
  },
  closeButton: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    color: theme.text,
  },
  tabContainer: {
    display: 'flex',
    gap: '6px',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.border}`,
    backgroundColor: theme.bgCard,
  },
  tab: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  tabActive: {
    backgroundColor: 'rgba(250, 204, 21, 0.15)',
    color: theme.warning,
  },
  tabActiveGreen: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    color: theme.success,
  },
  tabActivePurple: {
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    color: theme.purple,
  },
  subtitle: {
    color: theme.textSecondary,
    fontSize: '14px',
    margin: 0,
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'center',
  },
  cardsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
  },
  cardItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    marginBottom: '8px',
    backgroundColor: theme.bgCard,
  },
  cardImageWrapper: {
    position: 'relative',
    width: '56px',
    height: '78px',
    borderRadius: '6px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: theme.bg,
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mutualBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: theme.bgSheet,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'scale(0.7)',
  },
  cardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  cardName: {
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.text,
  },
  cardSet: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.textSecondary,
  },
  cardGrade: {
    fontSize: '11px',
    fontWeight: '600',
    color: theme.accent,
  },
  tradeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 14px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
    backgroundColor: theme.accent,
    color: '#ffffff',
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 14px',
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
    backgroundColor: theme.bgCard,
    color: theme.text,
  },
});

export default WishListMatchModal;
